import { useCallback, useMemo, useState } from 'react';
import { Pagination } from '@mews-commander/data-access';
import { DateTimeUtils, useAppClockService, useTimezone } from '@mews/framework';
import { INITIAL_PAGINATION_VALUES } from '../../cityLedger/constants';

export interface FilterContextValues<T extends {}> {
    filterValues: T;
    setFilterValue: (key: keyof T, newValue: string) => void;
    setFilterValues: (values: Partial<T>) => void;
    resetFilterValues: (values?: Partial<T>) => void;
}

export interface ExtendedFilterContextValues<T extends {}> extends FilterContextValues<T> {
    pagination: Pagination;
    setPagination: React.Dispatch<React.SetStateAction<Pagination>>;
    resetPagination: () => void;
    selectedDate: string;
    setSelectedDate: (date: string) => void;
}

export enum FilterContextNames {
    CITY_LEDGER = 'CityLedgerFilterContext',
    GUEST_LEDGER = 'GuestLedgerFilterContext',
    DEPOSIT_LEDGER = 'DepositLedgerFilterContext',
}

export const useFilterState = <T extends {}>(
    initialValues: T
): Pick<FilterContextValues<T>, 'filterValues' | 'resetFilterValues' | 'setFilterValue' | 'setFilterValues'> => {
    const [filterValues, setFilterValuesState] = useState<T>(initialValues);

    const setFilterValue = useCallback((key: keyof T, newValue: string | string[]) => {
        setFilterValuesState((prev) => ({
            ...prev,
            [key]: newValue,
        }));
    }, []);

    const setFilterValues = useCallback(
        (values: Partial<T>) => {
            setFilterValuesState((prevValues) => ({
                ...prevValues,
                ...values,
            }));
        },
        [setFilterValuesState]
    );

    const resetFilterValues = useCallback(
        (partialFilterValues?: Partial<T>) => {
            setFilterValues(partialFilterValues || initialValues);
        },
        [initialValues, setFilterValues]
    );

    return useMemo(
        () => ({
            filterValues,
            setFilterValue,
            setFilterValues,
            resetFilterValues,
        }),
        [filterValues, setFilterValue, setFilterValues, resetFilterValues]
    );
};

export const usePaginationState = (): Pick<
    ExtendedFilterContextValues<{}>,
    'pagination' | 'setPagination' | 'resetPagination'
> => {
    const [pagination, setPagination] = useState<Pagination>(INITIAL_PAGINATION_VALUES);

    const resetPagination = useCallback(() => {
        setPagination(INITIAL_PAGINATION_VALUES);
    }, []);
    return useMemo(() => ({ pagination, setPagination, resetPagination }), [pagination, resetPagination]);
};

export const useSelectedDateState = (
    selectedDateValue?: string
): Pick<ExtendedFilterContextValues<{}>, 'selectedDate' | 'setSelectedDate'> => {
    const appClockService = useAppClockService();
    const now = appClockService.getAppNowUtc().value;
    const timezone = useTimezone();

    const initialDateValue = useMemo(
        () => DateTimeUtils.dateTimeUtcStringToDateISOString(now, timezone),
        [now, timezone]
    );
    const [selectedDate, setSelectedDate] = useState<string>(selectedDateValue ?? initialDateValue);

    return useMemo(() => ({ selectedDate, setSelectedDate }), [selectedDate]);
};
